/**
 * English syllable counting by rule, with a short table for the words rules get wrong.
 *
 * Every counter of this kind is a heuristic; English spelling does not encode syllables.
 * This one counts vowel groups, then corrects for the patterns that move the count:
 * silent final -e, -ed and -es endings, vowel pairs that split (idea, create, usual,
 * radio), vowels that are really consonants (queen, language, year), and the
 * -tion/-sion/-cious endings that look like two vowels and sound like one. It is the
 * one syllable counter behind the Syllable Counter, the Readability Score Checker, the
 * Reading Time Calculator, the Complex Word Finder and the Feynman template, so they
 * cannot disagree about a word.
 *
 * `syllables-cmu.test.ts` checks it against the CMU Pronouncing Dictionary: a named
 * corpus of more than two hundred words, and the whole New General Service List with its
 * inflections as an accuracy floor. It is right for most everyday and academic
 * vocabulary, and wrong for a few borrowed and irregular words, which is why the finder
 * shows the count it used and lets the reader mark a word as fine.
 *
 * Where the dictionary and the CMU file disagree because a word can be said either way —
 * "fire" and "hour" as one syllable or two, "every" as two or three — this counter takes
 * the count a printed dictionary gives (one, one, two), and the test lists those words.
 */

const RE_STRIP = /[^a-z]/g;
const RE_COMPOUND_JOIN = /[-\u2010\u2011\u2012\u2013\u2014_/]+/;
const RE_MARKS = /\p{M}/gu;
const RE_VOWEL_GROUPS = /[aeiouy]+/g;
const RE_LE_END = /[^aeiouy]le$/;
const RE_SILENT_ED = /[^aeiouytd]ed$/;
const RE_LED_END = /[^aeiouyl]led$/;
const RE_SOME_COMPOUND = /^some(?=thing|one|body|tim|what|how|wher|place|day)/;
const RE_NO_PLURAL_S = /(?:[sxz]|ch|sh|[cg]e|ss)$/;
const RE_ES_AFTER_SIBILANT = /(?:[sxzcg]|ch|sh)es$/;
const RE_CONSONANT_LES = /[^aeiouy]les$/;
const RE_DROPPABLE_ES = /[^aeiouy]es$/;
const RE_VOWEL_GUE = /[aeiou]gue$/;
const RE_QU = /qu(?=[aeiouy])/g;
const RE_GU_VOWEL = /gu(?=[aeiy])/g;
const RE_INITIAL_Y = /^y(?=[aeiou])/;
const RE_UE_END = /ue$/;
const RE_DIAERESIS = /([aeiou])([ïëöüÿ])/g;
const RE_FINAL_ACCENT_E = /[éèê]$/;
const RE_SILENT_E_SUFFIX = /[^aeiouy]e(?=ful|less|ness|ly$|lin$)/g;
const RE_SILENT_E_MENT = /([^aeiouyl])e(?=ment)/g;

/** Pairs that sound as two syllables, found after the vowel groups are counted. */
const SPLITS: readonly RegExp[] = [
  // radio, video, curious, serious, various, period, previous
  /[bdfhkmprvz]io/g,
  /[bdfhklmnprvz]ia/g,
  /[cstg]ia(?!l|n|ns)/g,
  /flu(?=[ei])/g,
  /io(?=log|graph)/g,
  /(?:^|[^c])uo/g,
  /[bdfghklmnprstvz]eo/g,
  // diet, quiet, society, science, client, variety
  /[bcdfhkmpstvz]iet/g,
  /[bdfhklmnprvz]ie(?=nc|nt)/g,
  /[a-z]ie(?=ty)/g,
  /[bfhlmnrstvzcd]ium/g,
  /iu(?=m)/g,
  // playing, trying, seeing, pursuing: the i of -ing is its own syllable after a vowel
  /[aeiouy]ing$/g,
  // happier, easiest: -ier and -iest on a consonant (soldier and priest are listed)
  /[^aeiouy]ie(?:rs?|st)$/g,
  // criticism, prism, sarcasm: the final m is syllabic
  /(?:isms?|[^aeiouy]asm)$/g,
  // loyal, employer, foyer: oy and ay before a vowel are two syllables
  /[ao]ya/g,
  /[ao]yer(?:s|ed|ing)?$/g,
  /[bdfgklmprstvxz]ua/g,
  /^(?:re|pre)(?=o)/g,
];

/** Words the rules miss. Counts are for the word as written. */
const EXCEPTIONS: Readonly<Record<string, number>> = {
  maybe: 2,
  beyond: 2,
  ok: 2,
  okay: 2,
  feline: 2,
  any: 2,
  anyone: 3,
  anybody: 4,
  anything: 3,
  anywhere: 3,
  someone: 2,
  somebody: 3,
  something: 2,
  sometimes: 2,
  somewhere: 2,
  somehow: 2,
  somewhat: 2,
  everybody: 4,
  everywhere: 3,
  everyday: 3,
  elsewhere: 2,
  moreover: 3,
  household: 2,
  ago: 2,
  era: 2,
  via: 2,
  ion: 2,
  ions: 2,
  eon: 2,
  freer: 2,
  freest: 2,
  truer: 2,
  truest: 2,
  bluer: 2,
  bluest: 2,
  grayer: 2,
  grayest: 2,
  rely: 2,
  relying: 3,
  ruin: 2,
  ruined: 2,
  ruining: 3,
  royal: 2,
  fuel: 2,
  fueled: 2,
  museum: 3,
  employer: 3,
  soldier: 2,
  priest: 1,
  premier: 2,
  frontier: 2,
  cashier: 2,
  tier: 1,
  pier: 1,
  quieter: 3,
  quietest: 3,
  quieted: 3,
  quieting: 3,
  quietly: 3,
  persuaded: 3,
  persuading: 3,
  reacted: 3,
  reacting: 3,
  realized: 3,
  realised: 3,
  realizing: 4,
  realising: 4,
  realizes: 4,
  realises: 4,
  forecast: 2,
  forecasted: 3,
  forecasting: 3,
  coauthor: 3,
  coexist: 3,
  coworker: 3,
  coalition: 4,
  deactivate: 4,
  reappear: 3,
  reapply: 3,
  reassure: 3,
  reassess: 3,
  reenter: 3,
  reelect: 3,
  reevaluate: 5,
  reexamine: 4,
  reunion: 3,
  reunite: 3,
  reiterate: 4,
  reinforce: 3,
  reinvent: 3,
  preexisting: 4,
  preempt: 2,
  preemptive: 3,
  nevertheless: 4,
  nonetheless: 3,
  cement: 2,
  increment: 3,
  therefore: 2,
  wherefore: 2,
  framework: 2,
  frameworks: 2,
  bibliography: 5,
  cough: 1,
  area: 3,
  areas: 3,
  idea: 3,
  ideas: 3,
  real: 1,
  really: 2,
  create: 2,
  created: 3,
  creating: 3,
  creation: 3,
  creative: 3,
  creativity: 5,
  creator: 3,
  theory: 3,
  theories: 3,
  theatre: 3,
  theater: 3,
  science: 2,
  sciences: 3,
  scientist: 3,
  scientific: 4,
  poem: 2,
  poet: 2,
  poetry: 3,
  being: 2,
  seeing: 2,
  doing: 2,
  going: 2,
  our: 1,
  hour: 1,
  fire: 1,
  hire: 1,
  wire: 1,
  tire: 1,
  entire: 2,
  higher: 2,
  lower: 2,
  every: 2,
  everyone: 3,
  everything: 3,
  different: 3,
  family: 3,
  families: 3,
  business: 2,
  interest: 3,
  interested: 4,
  interesting: 4,
  literature: 4,
  temperature: 4,
  average: 3,
  comfortable: 4,
  vegetable: 4,
  evening: 2,
  several: 3,
  general: 3,
  generally: 3,
  usually: 4,
  actually: 4,
  eventually: 5,
  especially: 4,
  naturally: 4,
  separate: 3,
  different_: 3,
  evidence: 3,
  evident: 3,
  experience: 4,
  experiences: 5,
  science_: 2,
  language: 2,
  languages: 3,
  quiet: 2,
  quite: 1,
  queue: 1,
  persuade: 2,
  suede: 1,
  genuine: 3,
  guarantee: 3,
  fluorescent: 3,
  people: 2,
  leopard: 2,
  yesterday: 3,
  prayer: 1,
  player: 2,
  layer: 2,
  mayor: 2,
  lawyer: 2,
  buyer: 2,
  employee: 3,
  create_: 2,
  recreate: 3,
  react: 2,
  reaction: 3,
  reactions: 3,
  readily: 3,
  already: 3,
  cooperate: 4,
  cooperation: 5,
  coordinate: 4,
  coordination: 5,
  coincide: 3,
  reality: 4,
  realize: 3,
  realise: 3,
  realistic: 4,
  realm: 1,
  deal: 1,
  dealt: 1,
  ideal: 3,
  idealist: 4,
  beautiful: 3,
  beauty: 2,
  using: 2,
  used: 1,
  uses: 2,
  use: 1,
  user: 2,
  users: 2,
  those: 1,
  whose: 1,
  these: 1,
  here: 1,
  there: 1,
  where: 1,
  were: 1,
  more: 1,
  some: 1,
  come: 1,
  done: 1,
  one: 1,
  once: 1,
  give: 1,
  live: 1,
  have: 1,
  gone: 1,
  love: 1,
  above: 2,
  become: 2,
  because: 2,
  analysis: 4,
  analyses: 4,
  hypothesis: 4,
  hypotheses: 4,
  thesis: 2,
  theses: 2,
  basis: 3,
  bases: 2,
  synthesis: 4,
  diagnosis: 5,
  nuclear: 3,
  negligible: 4,
  video: 3,
  radio: 3,
  studio: 3,
  patio: 3,
  ratio: 3,
  ratios: 3,
  audio: 3,
  gradual: 3,
  actual: 3,
  annual: 3,
  manual: 3,
  usual: 3,
  casual: 3,
  visual: 3,
  virtual: 3,
  individual: 5,
  situation: 4,
  quantitative: 4,
  qualitative: 4,
  criteria: 4,
  criterion: 4,
  phenomenon: 4,
  phenomena: 4,
  media: 3,
  medium: 3,
  mediums: 3,
  curriculum: 4,
  consortium: 4,
  stadium: 3,
  premium: 3,
  tedious: 3,
  religion: 3,
  religious: 3,
  region: 2,
  regions: 2,
  regional: 3,
  nation: 2,
  national: 3,
  international: 5,
  union: 2,
  onion: 2,
  million: 2,
  billion: 2,
  opinion: 3,
  companion: 3,
  champion: 3,
  genius: 2,
  serious: 3,
  various: 3,
  previous: 3,
  obvious: 3,
  curious: 3,
  period: 3,
  periods: 3,
  senior: 2,
  junior: 2,
  behavior: 3,
  behaviour: 3,
  superior: 4,
  interior: 4,
  exterior: 4,
  anterior: 4,
  inferior: 4,
  material: 4,
  materials: 4,
  memorial: 4,
  editorial: 5,
  official: 3,
  special: 2,
  specially: 3,
  social: 2,
  society: 4,
  anxiety: 4,
  variety: 4,
  variation: 4,
  variable: 4,
  variables: 4,
  patient: 2,
  patients: 2,
  ancient: 2,
  efficient: 3,
  sufficient: 3,
  deficient: 3,
  proficient: 3,
  science__: 2,
  client: 2,
  clients: 2,
  quotient: 3,
  giant: 2,
  diet: 2,
  riot: 2,
  violent: 3,
  violence: 3,
  violin: 3,
  pioneer: 3,
  biology: 4,
  biological: 5,
  geology: 4,
  geography: 4,
  geometry: 4,
  geometric: 4,
  neon: 2,
  neutral: 2,
  neuron: 2,
  neurons: 2,
  neuroscience: 4,
  lion: 2,
  poised: 1,
  coerce: 2,
  chaos: 2,
  chaotic: 3,
  aerial: 3,
  aerobic: 3,
  aware: 2,
  awareness: 3,
  eliminate: 4,
  elimination: 5,
  evaluate: 4,
  evaluation: 5,
  valuable: 4,
  value: 2,
  values: 2,
  valued: 2,
  continue: 3,
  continuous: 4,
  continual: 4,
  issue: 2,
  issues: 2,
  tissue: 2,
  residue: 3,
  venue: 2,
  avenue: 3,
  statue: 2,
  virtue: 2,
  unique: 2,
  technique: 2,
  critique: 2,
  boutique: 2,
  antique: 2,
  league: 1,
  colleague: 2,
  colleagues: 2,
  vague: 1,
  rogue: 1,
  tongue: 1,
  plague: 1,
  fatigue: 2,
  intrigue: 2,
  dialogue: 3,
  catalogue: 3,
  analogue: 3,
  monologue: 3,
  prologue: 2,
  epilogue: 3,
  argue: 2,
  argued: 2,
  argues: 2,
  arguing: 3,
  argument: 3,
  arguments: 3,
};

function letters(word: string): string {
  const spelled = word
    .normalize("NFC")
    .toLowerCase()
    .replace(RE_DIAERESIS, "$1x$2")
    .replace(RE_FINAL_ACCENT_E, "ee");
  return spelled.normalize("NFD").replace(RE_MARKS, "").replace(RE_STRIP, "");
}

function trimEnding(word: string): string {
  if (word.endsWith("e") && !RE_LE_END.test(word) && word.length > 2) {
    return word.slice(0, -1);
  }
  // handled, settled, struggled: the d goes, the syllabic l stays.
  if (RE_LED_END.test(word) && word.length > 4) {
    return word.slice(0, -1);
  }
  if (RE_SILENT_ED.test(word) && word.length > 3) {
    return word.slice(0, -2);
  }
  if (
    word.endsWith("es") &&
    word.length > 3 &&
    !RE_ES_AFTER_SIBILANT.test(word) &&
    !RE_CONSONANT_LES.test(word) &&
    RE_DROPPABLE_ES.test(word)
  ) {
    return word.slice(0, -2);
  }
  return word;
}

function vowelGroups(word: string): number {
  return word.match(RE_VOWEL_GROUPS)?.length ?? 0;
}

/**
 * The table's count for the word, or for its plural when only the singular is listed
 * ("poems" from "poem"; "boxes" and "churches" gain a syllable, so they are not derived).
 */
function listedCount(word: string): number | undefined {
  const known = EXCEPTIONS[word];
  if (known !== undefined) {
    return known;
  }
  if (word.endsWith("s") && !word.endsWith("ss")) {
    const base = word.slice(0, -1);
    const baseCount = EXCEPTIONS[base];
    if (baseCount !== undefined && !RE_NO_PLURAL_S.test(base)) {
      return baseCount;
    }
  }
  return undefined;
}

function countOneWord(rawWord: string): number {
  const word = letters(rawWord);
  if (word.length === 0) {
    return 0;
  }
  const known = listedCount(word);
  if (known !== undefined) {
    return known;
  }
  let body = word;
  if (RE_VOWEL_GUE.test(body)) {
    body = body.replace(RE_UE_END, "");
  }
  body = trimEnding(body)
    .replace(RE_SOME_COMPOUND, "som")
    .replace(RE_SILENT_E_MENT, "$1")
    .replace(RE_SILENT_E_SUFFIX, (match) => match.slice(0, 1))
    .replace(RE_QU, "q")
    .replace(RE_GU_VOWEL, "g")
    .replace(RE_INITIAL_Y, "");
  let count = vowelGroups(body);
  for (const split of SPLITS) {
    count += body.match(split)?.length ?? 0;
  }
  return Math.max(count, 1);
}

/**
 * Syllables in one word (letters only; anything else is ignored). At least 1 for a word.
 * A hyphenated or slashed compound is counted part by part, so "state-of-the-art" is
 * four, not whatever the run-together spelling happens to produce.
 */
export function countSyllables(rawWord: string): number {
  let total = 0;
  for (const part of rawWord.split(RE_COMPOUND_JOIN)) {
    total += countOneWord(part);
  }
  return total;
}

const RE_ING = /ing$/;
const RE_SUFFIX_ED = /[td]ed$/;
const RE_SUFFIX_ES = /(?:[sxzcg]|ch|sh)es$/;

/**
 * Syllables as the Gunning fog index counts them for the "complex word" test: a verb
 * ending does not make a word complex, so -ing, a pronounced -ed and a pronounced -es
 * are taken off the count ("walking", "created" and "judges" are not three-syllable
 * words). The word's own syllables are never reduced below 1.
 */
export function complexitySyllables(rawWord: string): number {
  const word = letters(rawWord);
  const total = countSyllables(word);
  let suffix = 0;
  if (word.length > 5 && RE_ING.test(word)) {
    suffix = 1;
  } else if (
    word.length > 4 &&
    (RE_SUFFIX_ED.test(word) || RE_SUFFIX_ES.test(word))
  ) {
    suffix = 1;
  }
  return Math.max(total - suffix, 1);
}
